import { QUICK_PRICER_FORM_CONFIG } from "../components/quick-pricer-form/quickPricer.form.config";
import { formatCurrency } from "./format-currency";

const ALL_QUICK_PRICER_FIELDS = QUICK_PRICER_FORM_CONFIG.sections
	.filter(Boolean)
	.flatMap((section) => section.fields || []);

const findFieldConfig = (fieldName) =>
	ALL_QUICK_PRICER_FIELDS.find((field) => field?.name === fieldName);

export const getFieldLabelText = (fieldName) =>
	findFieldConfig(fieldName)?.label?.trim() || fieldName;

// LC-324 "Required PDF Content" lists an explicit, closed set of fields per
// section - not every field the live form collects (e.g. the per-bucket
// mortgage-lates counters and the ARM/product-type helper fields are
// intentionally left out here, matching the ticket text and mockup).
export const QUICK_PRICER_PRINT_SECTIONS = [
	{
		title: "Loan Information",
		fieldNames: [
			"incomeVerificationType",
			"debtToIncomeRatio",
			"debtServiceCoverageRatio",
			"loanPurpose",
			"purchasePrice",
			"estimatedValue",
			"loanAmount",
			"closedEndSecondLienAmt",
			"helocLineAmount",
			"helocDrawnAmount",
			"cashoutAmount",
			"ltv",
			"cltv",
			"hcltv",
			"interestOnly",
			"prePaymentPenalty",
			"escrowWaiver",
		],
	},
	{
		title: "Borrower Information",
		fieldNames: [
			"citizenship",
			"firstTimeHomeBuyer",
			"nonoccupanctCoborrower",
			"selfEmployed",
			"reserve",
			"financedProperties",
		],
	},
	{
		title: "Credit Profile",
		fieldNames: [
			"qualifyingCreditScore",
			"housingEventType",
			"housingEventSeasoning",
			"bankruptcyType",
			"bankruptcyOutcome",
			"bankruptcySeasoning",
			"mortgageLatePayments",
		],
	},
	{
		title: "Property Information",
		fieldNames: [
			"propertyType",
			"occupancy",
			"shortTermRental",
			"numberofUnits",
			"numberofStories",
			"state",
			"county",
			"city",
			"zipcode",
		],
	},
	{
		title: "Product & Pricing Search Filters",
		fieldNames: [
			"loanTerms",
			"amortizationTypes",
			"ARMFixedTerms",
			"desiredRate",
			"desiredPrice",
			"desiredLockPeriod",
			"lenderPaidvsBorrowerPaid",
			"productTypes",
			"loanType",
			"lienType",
		],
	},
];

const isEmptyValue = (value) =>
	value === null ||
	value === undefined ||
	value === "" ||
	(Array.isArray(value) && value.length === 0);

const resolveFieldOptions = (field, formState) => {
	if (!field?.options) return null;
	return typeof field.options === "function"
		? field.options(formState)
		: field.options;
};

const formatMaskedValue = (field, value) => {
	switch (field?.maskType) {
		case "currency":
			return formatCurrency.format(Number(value));
		case "percentage":
			return `${Number(value).toFixed(3)}%`;
		case "ratio":
			return `${Number(value)}`;
		default:
			return String(value);
	}
};

// Ticket LC-324: "Unanswered or empty fields are shown as blank or None
// rather than omitted" - applied consistently across every field type here.
export const getFieldDisplayLabel = (fieldName, formState) => {
	const field = findFieldConfig(fieldName);
	const value = formState?.[fieldName];

	if (isEmptyValue(value)) return "None";

	if (!field) return String(value);

	const options = resolveFieldOptions(field, formState);

	if (Array.isArray(options)) {
		if (Array.isArray(value)) {
			const labels = value.map(
				(item) => options.find((opt) => opt.value === item)?.label || item
			);
			return labels.length ? labels.join(", ") : "None";
		}

		const match = options.find((opt) => opt.value === value);
		return match?.label ?? String(value);
	}

	if (field.component === "masked") {
		return formatMaskedValue(field, value);
	}

	if (typeof value === "boolean") return value ? "Yes" : "No";

	return String(value);
};

export const formatGeneratedTimestamp = (date = new Date()) => {
	const datePart = date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
		timeZone: "America/New_York",
	});

	const timePart = date.toLocaleTimeString("en-US", {
		hour: "numeric",
		minute: "2-digit",
		timeZone: "America/New_York",
		timeZoneName: "short",
	});

	return `${datePart} · ${timePart}`;
};
